import { inject } from '@angular/core';
import type { HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import type { Observable } from 'rxjs';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

// withCredentials:true es obligatorio en toda la API porque /auth/refresh depende de la
// cookie httpOnly `vendia_refresh` (CLAUDE.md §7). El accessToken vive en memoria en
// AuthService, nunca en localStorage.
//
// Orden real en app.config.ts: [errorInterceptor, authInterceptor, loadingInterceptor]. Los
// interceptors ven la REQUEST en ese orden pero la RESPUESTA/ERROR en el orden INVERSO — este
// interceptor queda más cerca del backend real que errorInterceptor, así que acá SIEMPRE
// llega el HttpErrorResponse crudo (nunca el NormalizedApiError ya transformado). Si el orden
// se invierte, el `error instanceof HttpErrorResponse` de abajo deja de ser cierto nunca y el
// refresh-on-401 no se dispara jamás.
function attach(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  return req.clone({
    withCredentials: true,
    setHeaders: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const token = authService.accessToken();

  // Todo /auth/* SALVO /auth/me queda afuera del refresh-and-retry: son endpoints públicos
  // (login, register, forgot/reset-password) o el propio refresh/logout, y un 401 ahí es
  // siempre un error de negocio real (credenciales/token inválidos), nunca "tu sesión
  // expiró". Bug real encontrado probando contra backend real: sin esta exclusión completa
  // (antes solo cubría /auth/refresh y /auth/login), un 401 de /auth/logout — que ocurre
  // siempre que el usuario cierra sesión sin tener ya un token válido — disparaba un intento
  // de refresh que también fallaba, lo que llamaba a logout() otra vez, que volvía a mandar
  // /auth/logout, que volvía a fallar 401... un loop infinito de red que además redirigía a
  // /auth/login en cada vuelta, pisando cualquier login legítimo que el usuario hiciera
  // mientras tanto. Mismo problema en /auth/reset-password: un 401 por token inválido
  // terminaba redirigiendo a /auth/login en vez de mostrar el error en la propia página.
  const isPublicAuthEndpoint = req.url.includes('/auth/') && !req.url.includes('/auth/me');

  return next(attach(req, token)).pipe(
    catchError((error: unknown): Observable<HttpEvent<unknown>> => {
      // Además de excluir los endpoints públicos de arriba, solo tiene sentido intentar un
      // refresh si la request que falló SÍ llevaba un accessToken (el usuario creía tener
      // sesión) — un 401 en una llamada sin token (endpoint público futuro, o una request ya
      // disparada después de un logout) no es una sesión expirada, es un 401 de negocio.
      if (isPublicAuthEndpoint || !token || !(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      return authService.refreshToken().pipe(
        switchMap((newToken) => next(attach(req, newToken))),
        catchError((refreshError: unknown) => {
          // Solo es una sesión realmente inválida si /auth/refresh respondió 401 de verdad
          // (refresh token vencido/revocado). Un error de red o un 5xx (ej. el backend
          // reiniciando por un deploy — pm2 restart tarda unos segundos) es transitorio, no una
          // sesión rota: desloguear igual sería un falso positivo ("se cerró sesión sola") pese
          // a que la sesión en el servidor sigue intacta y un reintento habría funcionado.
          const isGenuineSessionInvalid = refreshError instanceof HttpErrorResponse && refreshError.status === 401;
          if (isGenuineSessionInvalid) {
            authService.logout();
            router.navigateByUrl('/auth/login');
          }
          return throwError(() => error);
        }),
      );
    }),
  );
};
